import type {
  ProvenanceDetailResponse,
  ProvenanceEntryDto,
} from '@vivport/backend/types';
import type { AgentHistoryItem, ProcessRunTimelineItem } from './provenance-entry-item';

// ============================================================================
// Shared helpers
// ============================================================================

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

function readPositiveIntegerArray(record: Record<string, unknown>, key: string): number[] {
  const value = record[key];
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is number => typeof entry === 'number' && Number.isInteger(entry) && entry > 0
  );
}

function collectPositiveIntegersFromArray(value: unknown, sink: number[], seen: Set<number>): void {
  if (!Array.isArray(value)) return;
  for (const entry of value) {
    if (typeof entry === 'number' && Number.isInteger(entry) && entry > 0 && !seen.has(entry)) {
      seen.add(entry);
      sink.push(entry);
    }
  }
}

// ============================================================================
// PROCESS_RUN projection
// ============================================================================

const CREATED_ID_ARRAY_KEYS = [
  'createdContentIds',
  'updatedContentIds',
  'createdFileContentIds',
  'createdEventIds',
  'createdAnchorIds',
] as const;

const CREATED_ID_SCALAR_KEYS = ['createdContentId', 'updatedContentId'] as const;

function collectCreatedContentIds(payload: Record<string, unknown>): number[] {
  const ordered: number[] = [];
  const seen = new Set<number>();
  const output = payload.output;
  if (isPlainObject(output)) {
    for (const key of CREATED_ID_ARRAY_KEYS) {
      collectPositiveIntegersFromArray(output[key], ordered, seen);
    }
    for (const key of CREATED_ID_SCALAR_KEYS) {
      const candidate = output[key];
      if (typeof candidate === 'number' && Number.isInteger(candidate) && candidate > 0 && !seen.has(candidate)) {
        seen.add(candidate);
        ordered.push(candidate);
      }
    }
  }
  for (const key of CREATED_ID_ARRAY_KEYS) {
    collectPositiveIntegersFromArray(payload[key], ordered, seen);
  }
  return ordered;
}

function summarizePhase(phase: string, kind: string): string {
  switch (phase) {
    case 'worker_claimed':
      return 'Worker claimed the run';
    case 'agent_run_started':
      return 'Agent run started';
    case 'message_import_summary':
      return 'Message import summary';
    case 'anchor_decision':
      return 'Anchor decision';
    case '':
      return kind;
    default:
      return phase;
  }
}

function buildTriggerReceived(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  const triggerType = readString(payload, 'triggerType') ?? '';
  return {
    id: `trigger-${entry.id}`,
    kind: 'event_trigger_received',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    triggerType,
    rawDetail: entry.payload,
  };
}

function buildToolCall(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  const toolCallId = readNumber(payload, 'toolCallId');
  const toolKey = readString(payload, 'toolKey') ?? 'unknown';
  const toolStatus = readString(payload, 'toolStatus') ?? 'UNKNOWN';
  const input = payload.input;
  const output = payload.output;
  const errorValue = payload.error;
  return {
    id: `tool-${entry.id}`,
    kind: 'event_tool_call',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    toolCallId,
    toolKey,
    toolStatus,
    entryKind: entry.kind,
    status: entry.status,
    input,
    output,
    error: typeof errorValue === 'string' ? errorValue : entry.error,
    startedAt: readString(payload, 'startedAt'),
    finishedAt: readString(payload, 'finishedAt'),
    leaseAutonomy: payload.leaseAutonomy === true,
    rawDetail: entry.payload,
  };
}

function buildExecutionStarted(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  const phaseRaw = readString(payload, 'phase');
  const phase = phaseRaw ?? entry.kind;
  return {
    id: `started-${entry.id}`,
    kind: 'event_execution_started',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    phase,
    status: entry.status,
    // The pipeline writes a real per-phase summary line; fall back to the generic
    // phase label only when an entry carries no body.
    summary: entry.body ?? summarizePhase(phase, entry.kind),
    rawDetail: entry.payload,
  };
}

function buildAnchorDecision(
  entry: ProvenanceEntryDto,
  payload: Record<string, unknown>,
): ProcessRunTimelineItem | null {
  const sourceType = readString(payload, 'sourceType');
  const groupingKey = readString(payload, 'groupingKey');
  const representativeLabel = readString(payload, 'representativeLabel');
  const decisionRaw = readString(payload, 'decision');
  if (sourceType === null || groupingKey === null || representativeLabel === null) {
    return null;
  }
  if (decisionRaw !== 'REUSE' && decisionRaw !== 'CREATE' && decisionRaw !== 'SKIP') {
    return null;
  }
  const anchorIdValue = payload.anchorId;
  const anchorId =
    typeof anchorIdValue === 'number' && Number.isInteger(anchorIdValue) && anchorIdValue > 0
      ? anchorIdValue
      : null;
  const anchorTitle = readString(payload, 'anchorTitle');
  const anchorCreatedValue = payload.anchorCreated;
  const anchorCreated = typeof anchorCreatedValue === 'boolean' ? anchorCreatedValue : false;
  const attachedCount = readNumber(payload, 'attachedCount') ?? 0;
  const skippedCount = readNumber(payload, 'skippedCount') ?? 0;
  const rationale = readStringArray(payload, 'rationale');
  const evidenceRaw = payload.evidence;
  const evidence = isPlainObject(evidenceRaw)
    ? {
        consideredCandidateAnchorIds: readPositiveIntegerArray(evidenceRaw, 'consideredCandidateAnchorIds'),
        llmModel: readString(evidenceRaw, 'llmModel') ?? 'unknown',
        llmLatencyMs: readNumber(evidenceRaw, 'llmLatencyMs') ?? 0,
      }
    : { consideredCandidateAnchorIds: [], llmModel: 'unknown', llmLatencyMs: 0 };
  return {
    id: `anchor-decision-${entry.id}`,
    kind: 'event_anchor_decision',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    schemaVersion: 1,
    sourceType,
    groupingKey,
    representativeLabel,
    decision: decisionRaw,
    anchorId,
    anchorTitle,
    anchorCreated,
    attachedCount,
    skippedCount,
    rationale,
    evidence,
    rawDetail: entry.payload,
  };
}

function buildExecutionCompleted(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  return {
    id: `completed-${entry.id}`,
    kind: 'event_execution_completed',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    agentRunId: readString(payload, 'agentRunId'),
    agentRunStatus: readString(payload, 'agentRunStatus'),
    rawDetail: entry.payload,
  };
}

function buildExecutionCancelled(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  return {
    id: `cancelled-${entry.id}`,
    kind: 'event_execution_cancelled',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    reason: readString(payload, 'reason'),
    agentRunId: readString(payload, 'agentRunId'),
    rawDetail: entry.payload,
  };
}

function buildExecutionFailed(entry: ProvenanceEntryDto): ProcessRunTimelineItem {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  const payloadErrorValue = payload.error;
  const payloadErrorMessage =
    typeof payloadErrorValue === 'string'
      ? payloadErrorValue
      : isPlainObject(payloadErrorValue)
        ? readString(payloadErrorValue, 'message') ?? ''
        : '';
  const errorMessage = entry.error ?? payloadErrorMessage;
  return {
    id: `failed-${entry.id}`,
    kind: 'event_execution_failed',
    entryId: String(entry.id),
    sequence: entry.sequence,
    occurredAt: entry.createdAt,
    errorMessage,
    reason: readString(payload, 'reason'),
    agentRunId: readString(payload, 'agentRunId'),
    rawDetail: entry.payload,
  };
}

function isToolCallPayload(payload: Record<string, unknown>): boolean {
  return readString(payload, 'projection') === 'agent_run_timeline' && readString(payload, 'entryType') === 'tool_call';
}

function projectProcessRunDetail(detail: ProvenanceDetailResponse): AgentHistoryItem[] {
  if (detail.subtypeData.kind !== 'PROCESS_RUN') return [];
  const subtype = detail.subtypeData;
  const items: AgentHistoryItem[] = [];
  let sawTerminal = false;

  for (const entry of detail.entries) {
    if (entry.kind === 'enqueued') continue;
    const payload = isPlainObject(entry.payload) ? entry.payload : null;

    if (entry.kind === 'trigger_received') {
      items.push(buildTriggerReceived(entry));
      continue;
    }

    if (entry.kind === 'execution_completed') {
      items.push(buildExecutionCompleted(entry));
      sawTerminal = true;
      continue;
    }

    if (entry.kind === 'execution_failed') {
      items.push(buildExecutionFailed(entry));
      sawTerminal = true;
      continue;
    }

    if (entry.kind === 'execution_cancelled') {
      items.push(buildExecutionCancelled(entry));
      sawTerminal = true;
      continue;
    }

    if (payload && isToolCallPayload(payload)) {
      items.push(buildToolCall(entry));
      const createdIds = collectCreatedContentIds(payload);
      for (const contentId of createdIds) {
        items.push({
          id: `created-${entry.id}-${contentId}`,
          kind: 'event_content_created',
          contentId,
          contentType: 'DOCUMENT',
        });
      }
      continue;
    }

    // Post-import pipeline phases, succeeded and failed alike, so the timeline shows
    // which phase a run stopped on rather than only that it stopped.
    if (entry.kind === 'pipeline_phase') {
      items.push(buildExecutionStarted(entry));
      continue;
    }

    if (entry.kind === 'execution_started') {
      if (payload && readString(payload, 'phase') === 'anchor_decision') {
        const anchorDecisionItem = buildAnchorDecision(entry, payload);
        if (anchorDecisionItem) {
          items.push(anchorDecisionItem);
          continue;
        }
      }
      items.push(buildExecutionStarted(entry));
      continue;
    }
  }

  const runtimeState = isPlainObject(subtype.latestRuntimeState) ? subtype.latestRuntimeState : null;
  const agentReply = runtimeState ? readString(runtimeState, 'agentReply') : null;
  const runtimeAgentRunId = runtimeState ? readString(runtimeState, 'agentRunId') : null;
  const runtimeAt = runtimeState ? readString(runtimeState, 'at') : null;
  const agentRunStatus = runtimeState ? readString(runtimeState, 'agentRunStatus') : null;

  if (agentReply && agentReply.length > 0) {
    const occurredAt = subtype.finishedAt ?? runtimeAt ?? new Date().toISOString();
    items.push({
      id: `agent-reply-${detail.provenanceId}`,
      kind: 'event_agent_reply',
      occurredAt,
      reply: agentReply,
      agentRunId: subtype.agentRunId ?? runtimeAgentRunId,
    });
  }

  if (agentRunStatus === 'PENDING_APPROVAL') {
    const occurredAt = subtype.finishedAt ?? runtimeAt ?? new Date().toISOString();
    items.push({
      id: `pending-approval-${detail.provenanceId}`,
      kind: 'event_pending_approval',
      occurredAt,
      agentRunId: subtype.agentRunId ?? runtimeAgentRunId,
      message: 'Agent run is awaiting approval.',
    });
  }

  if (!sawTerminal && subtype.finishedAt) {
    if (detail.status === 'SUCCEEDED') {
      items.push({
        id: `completed-synthetic-${detail.provenanceId}`,
        kind: 'event_execution_completed',
        entryId: null,
        sequence: null,
        occurredAt: subtype.finishedAt,
        agentRunId: subtype.agentRunId,
        agentRunStatus,
        rawDetail: runtimeState,
      });
    } else if (detail.status === 'FAILED' || detail.status === 'DEAD_LETTER') {
      items.push({
        id: `failed-synthetic-${detail.provenanceId}`,
        kind: 'event_execution_failed',
        entryId: null,
        sequence: null,
        occurredAt: subtype.finishedAt,
        errorMessage: subtype.error ?? 'Run ended without a terminal step',
        reason: runtimeState ? readString(runtimeState, 'reason') : null,
        agentRunId: subtype.agentRunId,
        rawDetail: runtimeState,
      });
    } else if (detail.status === 'CANCELLED') {
      items.push({
        id: `cancelled-synthetic-${detail.provenanceId}`,
        kind: 'event_execution_cancelled',
        entryId: null,
        sequence: null,
        occurredAt: subtype.finishedAt,
        reason: runtimeState ? readString(runtimeState, 'reason') : null,
        agentRunId: subtype.agentRunId,
        rawDetail: runtimeState,
      });
    }
  }

  return items;
}

// ============================================================================
// MANUAL_SESSION projection
// ============================================================================

function projectManualSessionEntry(entry: ProvenanceEntryDto): AgentHistoryItem | null {
  const payload = isPlainObject(entry.payload) ? entry.payload : {};
  const entryId = String(entry.id);

  switch (entry.kind) {
    case 'session_opened':
      return {
        id: `manual-opened-${entry.id}`,
        kind: 'manual_session_opened',
        entryId,
        sequence: entry.sequence,
        occurredAt: entry.createdAt,
      };
    case 'session_closed':
      return {
        id: `manual-closed-${entry.id}`,
        kind: 'manual_session_closed',
        entryId,
        sequence: entry.sequence,
        occurredAt: entry.createdAt,
        reason: readString(payload, 'reason'),
      };
    case 'content_created': {
      const contentId = readNumber(payload, 'contentId');
      if (contentId === null) return null;
      return {
        id: `manual-created-${entry.id}`,
        kind: 'manual_session_content_created',
        entryId,
        sequence: entry.sequence,
        occurredAt: entry.createdAt,
        contentId,
        contentType: readString(payload, 'contentType') ?? '',
      };
    }
    case 'content_updated': {
      const contentId = readNumber(payload, 'contentId');
      if (contentId === null) return null;
      return {
        id: `manual-updated-${entry.id}`,
        kind: 'manual_session_content_updated',
        entryId,
        sequence: entry.sequence,
        occurredAt: entry.createdAt,
        contentId,
        contentType: readString(payload, 'contentType') ?? '',
        changedFields: readStringArray(payload, 'changedFields'),
      };
    }
    case 'content_deleted': {
      const contentId = readNumber(payload, 'contentId');
      if (contentId === null) return null;
      return {
        id: `manual-deleted-${entry.id}`,
        kind: 'manual_session_content_deleted',
        entryId,
        sequence: entry.sequence,
        occurredAt: entry.createdAt,
        contentId,
        contentType: readString(payload, 'contentType') ?? '',
      };
    }
    default:
      return null;
  }
}

function projectManualSessionDetail(detail: ProvenanceDetailResponse): AgentHistoryItem[] {
  const items: AgentHistoryItem[] = [];
  for (const entry of detail.entries) {
    const mapped = projectManualSessionEntry(entry);
    if (mapped !== null) items.push(mapped);
  }
  return items;
}

// ============================================================================
// Unified dispatcher
// ============================================================================

// Background process work executes UNDER the chat apex (apex-attributed executions), and its
// WORKER-role entries land in the chat's provenance. The conversation itself renders through
// its own data path (getNeoChatConversation / neoConversationReducer); this projection
// surfaces only the background-work timeline those entries record.
function projectNeoChatBackgroundWork(detail: ProvenanceDetailResponse): AgentHistoryItem[] {
  const items: AgentHistoryItem[] = [];
  for (const entry of detail.entries) {
    if (entry.role !== 'WORKER') continue;
    const payload = isPlainObject(entry.payload) ? entry.payload : null;
    if (payload && isToolCallPayload(payload)) {
      items.push(buildToolCall(entry));
      continue;
    }
    if (entry.kind === 'execution_completed' || entry.kind === 'execution_succeeded') {
      items.push(buildExecutionCompleted(entry));
      continue;
    }
    if (entry.kind === 'execution_failed') {
      items.push(buildExecutionFailed(entry));
      continue;
    }
    if (entry.kind === 'execution_cancelled') {
      items.push(buildExecutionCancelled(entry));
    }
  }
  return items;
}

export function mapProvenanceDetailToHistoryItems(
  detail: ProvenanceDetailResponse | null,
): AgentHistoryItem[] {
  if (!detail) return [];
  switch (detail.subtypeData.kind) {
    case 'NEO_CHAT':
      return projectNeoChatBackgroundWork(detail);
    case 'PROCESS_RUN':
      return projectProcessRunDetail(detail);
    case 'MANUAL_SESSION':
      return projectManualSessionDetail(detail);
  }
}
